import { DateTime } from '@ksyos/date-time';
import {
    Badge,
    Box,
    Button,
    Card,
    Icon,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
} from '@ksyos/design-system';
import { isNumber } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type { IBatchExactError } from '@common/interfaces';
import type { IBatchGetOne } from '@frontend/components/interfaces/batchGetOne';
import { BatchType, ExactBatchExportStatus } from '@common/enums';
import { DisplayFullName } from '@frontend/components/elements/DisplayFullName';
import {
    DownloadProgressFeedbackType,
    ExportDownloadProgress,
} from '@frontend/components/elements/ExportDownloadProgress';
import { ListPager } from '@frontend/components/elements/ListPager';
import { Modal } from '@frontend/components/elements/modals/Modal';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { AbortableFetch } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { useArrayUtils } from '@frontend/utils/hooks';

interface IInformationColumn<T> {
    isDate?: boolean;
    key: keyof T;
    label: string;
    translateKeyPrefix?: string;
}

interface IBatchesTableProps<T> {
    allowRerun?: boolean;
    batchType: BatchType;
    informationColumns?: IInformationColumn<T>[];
    marginTop?: `${number}px`;
    onExport: (runNumber: string) => Promise<void>;
    showIterativeBatches?: boolean;
    title?: string;
}

interface IRerunningBatch {
    exactClientEventId?: string;
    exportDownloadId?: string;
    runNumber: number;
}

interface IPaymentsExportState {
    allBatchesAreComplete?: boolean;
    batches: IBatchGetOne[];
    itemsPerPage: number;
    loading: boolean;
    modalRunNumber: number | null;
    page: number;
    rerunningBatches: IRerunningBatch[];
    total: number;
}

export function BatchesTable<T>(props: IBatchesTableProps<T>) {
    const abortableFetch = useMemo(() => new AbortableFetch(), []);

    const [state, setState] = useState<IPaymentsExportState>({
        loading: false,
        page: 1,
        itemsPerPage: 10,
        total: 0,
        batches: [],
        rerunningBatches: [],
        modalRunNumber: null,
    });

    const [isPayoutOrInvoice, setIsPayoutOrInvoice] = useState<boolean>(false);

    const { arrayToCommaSeparatedString } = useArrayUtils();

    // initial loading of batches
    useEffect(() => {
        handleRefreshBatches();
        compactErrors();
        setIsPayoutOrInvoice([BatchType.payout, BatchType.invoice].includes(props.batchType));
    }, [props.batchType]);

    return (
        <>
            <Card.Header
                action={
                    <Button
                        iconLeft="refresh"
                        onClick={handleRefreshBatches}
                        variant="link"
                    >
                        {getSelectedLanguage().common.refresh}
                    </Button>
                }
                badge={
                    props.showIterativeBatches
                        ? {
                              color: state.allBatchesAreComplete ? 'green' : 'orange',
                              icon: state.allBatchesAreComplete ? 'information' : 'warning',
                              text: state.allBatchesAreComplete
                                  ? getSelectedLanguage().exact.allBatchesComplete
                                  : getSelectedLanguage().exact.someBatchesUncomplete,
                          }
                        : undefined
                }
                title={isPayoutOrInvoice ? getSelectedLanguage().common.previousExports : props.title}
            />

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{getSelectedLanguage().export.runNumber}</TableHeader>
                            {props.showIterativeBatches && <TableHeader>Status</TableHeader>}
                            <TableHeader>{getSelectedLanguage().export.runDate}</TableHeader>
                            <TableHeader>{getSelectedLanguage().practitioner.practitioner}</TableHeader>
                            {props.informationColumns &&
                                props.informationColumns.map((column) => (
                                    <TableHeader key={column.key as string}>{column.label}</TableHeader>
                                ))}
                            <TableHeader></TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {state.batches.map((batch, i) => (
                            <TableRow key={`batch-${i}`}>
                                <TableCell>
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                    >
                                        <Text
                                            size="sm"
                                            textAlign="center"
                                        >
                                            {batch.runNumber}
                                        </Text>
                                        {!batchIsRerunning(batch.runNumber) && !!batch.exactExportErrors?.length && (
                                            <Icon
                                                icon="information"
                                                onClick={() =>
                                                    setState((prev) => ({ ...prev, modalRunNumber: batch.runNumber }))
                                                }
                                                style={{ cursor: 'pointer', marginLeft: '10px' }}
                                            />
                                        )}
                                    </Box>
                                </TableCell>
                                {props.showIterativeBatches && <TableCell>{getBatchBadge(batch)}</TableCell>}
                                <TableCell>
                                    <NullableFormattedDate value={batch.runDate} />
                                </TableCell>
                                <TableCell>
                                    <Link
                                        to={'/practitioners/' + batch.batchExecutor.id}
                                        variant="link"
                                    >
                                        <DisplayFullName {...batch.batchExecutor} />
                                    </Link>
                                </TableCell>
                                {props.informationColumns &&
                                    props.informationColumns.map((column) => (
                                        <TableCell key={`${String(column.key)}-row-${i}`}>
                                            {displayUsedFilter(batch, column)}
                                        </TableCell>
                                    ))}
                                <TableCell>
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                        justifyContent="flex-end"
                                    >
                                        <IconButton
                                            icon="download"
                                            iconColor="default"
                                            onClick={async () => {
                                                if (batch.exportDownload.id) {
                                                    window.open(
                                                        `/api/exportDownloads/${batch.exportDownload.id}/${batch.exportDownload.filename}`
                                                    );
                                                } else {
                                                    props.onExport(batch.id);
                                                }
                                            }}
                                        ></IconButton>

                                        {props.showIterativeBatches &&
                                            batch.exactExportStatus === ExactBatchExportStatus.uncomplete && (
                                                <IconButton
                                                    icon="refresh"
                                                    iconColor="default"
                                                    isDisabled={batchIsRerunning(batch.runNumber) || !props.allowRerun}
                                                    onClick={async () => await handleRerun(batch)}
                                                >
                                                    {getSelectedLanguage().exact.rerun}
                                                </IconButton>
                                            )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Card.Footer>
                <ListPager
                    isDisabled={state.loading}
                    itemsPerPage={state.itemsPerPage}
                    onChangePage={async (page) => {
                        await loadBatches(page);
                    }}
                    page={state.page}
                    total={state.total}
                />
            </Card.Footer>
            <Modal
                actionButtons={
                    state.batches.find((batch) => batch.runNumber === state.modalRunNumber)?.exactExportStatus ===
                    ExactBatchExportStatus.completeWithErrorsOrWarnings
                        ? undefined
                        : [
                              <Button onClick={() => handleDismissExactErrors(state.modalRunNumber)}>
                                  Fouten negeren
                              </Button>,
                          ]
                }
                isOpen={!!state.modalRunNumber}
                onClose={() => setState((prev) => ({ ...prev, modalRunNumber: null }))}
                size="large"
                title={`R${state.modalRunNumber} - Exact export errors`}
            >
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>Topic</TableHeader>
                                <TableHeader>Reference</TableHeader>
                                <TableHeader>Description</TableHeader>
                                <TableHeader></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.batches
                                .find((b) => b.runNumber === state.modalRunNumber)
                                ?.exactExportErrors?.map((error, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{error.topic}</TableCell>
                                        <TableCell>{error.reference}</TableCell>
                                        <TableCell>{error.description}</TableCell>
                                        <TableCell>
                                            {error.count! > 0 ? (
                                                <Badge
                                                    shape="circle"
                                                    text={`${error.count}`}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Modal>
        </>
    );

    function handleRefreshBatches() {
        const page = 1;

        setState((prev) => ({ ...prev, page, total: 0 }));

        loadBatches(page);
    }

    async function loadBatches(page: number) {
        const url = `/api/batches?page=${page}&limit=10&batchType=${props.batchType}`;

        setState((prev) => ({ ...prev, loading: true }));

        const result = await abortableFetch.fetchJson(url, 'GET');

        if (result.json.items) {
            setState((prev) => ({
                ...prev,
                page,
                loading: false,
                total: result.json.total,
                batches: result.json.items,
                allBatchesAreComplete: !(result.json.items as IBatchGetOne[]).some(
                    (batch) => batch.exactExportStatus === ExactBatchExportStatus.uncomplete
                ),
                rerunningBatches: [],
            }));
        }
    }

    async function compactErrors() {
        const compactBatches: IBatchGetOne[] = state.batches.map((batch) => {
            const compactedErrors: IBatchExactError[] = [];

            batch.exactExportErrors?.forEach((error) => {
                const errorFound = compactedErrors.find(
                    (duplicatedError) =>
                        error.topic === duplicatedError.topic && error.description === duplicatedError.description
                );
                if (errorFound) {
                    const errorIndex = compactedErrors.indexOf(errorFound);
                    compactedErrors[errorIndex].count = isNumber(compactedErrors[errorIndex].count)
                        ? compactedErrors[errorIndex].count! + 1
                        : 0;
                } else {
                    compactedErrors.push(error);
                }
            });

            return { ...batch, exactExportErrors: compactedErrors };
        });

        setState((prev) => ({ ...prev, batches: compactBatches }));
    }

    function displayUsedFilter(batch: IBatchGetOne, informationColumn: IInformationColumn<T>) {
        let usedFilter: JSX.Element | string | string[] | null = null;

        if (batch.usedFilters) {
            const currentFilter: string = batch.usedFilters[informationColumn.key];

            if (currentFilter) {
                usedFilter = informationColumn.translateKeyPrefix
                    ? getSelectedLanguage()[informationColumn.translateKeyPrefix as keyof typeof getSelectedLanguage][
                          currentFilter
                      ]
                    : currentFilter;

                if (informationColumn.isDate) {
                    usedFilter = DateTime.parse(usedFilter, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY');
                }
            }
        }

        if (Array.isArray(usedFilter)) {
            usedFilter = arrayToCommaSeparatedString(
                usedFilter.map((item) => getSelectedLanguage().payoutType[item as keyof typeof getSelectedLanguage])
            ) as unknown as string[];
        }

        return usedFilter;
    }

    async function handleRerun(batch: IBatchGetOne) {
        const result = await abortableFetch.fetchJson(`/api/batches/${batch.id}/rerun`, 'GET');

        setState((prev) => ({
            ...prev,
            rerunningBatches: [
                ...prev.rerunningBatches,
                {
                    runNumber: batch.runNumber,
                    exportDownloadId: result.json.exportDownloadId as string,
                    exactClientEventId: result.json.exactClientEventId as string,
                },
            ],
        }));
    }

    function batchIsRerunning(runNumber: number) {
        return !!state.rerunningBatches.find((rerunningBatch) => rerunningBatch.runNumber === runNumber);
    }

    function getBatchBadge(batch: IBatchGetOne) {
        if (batchIsRerunning(batch.runNumber)) {
            return (
                <>
                    <Box
                        display="flex"
                        flexDirection="row"
                    >
                        <Badge
                            color="cyan"
                            icon="waiting"
                            text={getSelectedLanguage().exact.rerunning}
                        />
                        <ExportDownloadProgress
                            downloadProcessFeedbackType={DownloadProgressFeedbackType.percentageText}
                            exportDownloadId={
                                state.rerunningBatches.find(
                                    (rerunningBatch) => rerunningBatch.runNumber === batch.runNumber
                                )?.exportDownloadId
                            }
                            onFinished={async () => {
                                await loadBatches(state.page);
                                setState((prev) => ({
                                    ...prev,
                                    rerunningBatches: [
                                        ...prev.rerunningBatches.filter(
                                            (rerunningBatch) => rerunningBatch.runNumber !== batch.runNumber
                                        ),
                                    ],
                                }));
                            }}
                        />
                    </Box>
                </>
            );
        }

        if (!batch.exactExportStatus) {
            return;
        }

        if (batch.exactExportStatus === ExactBatchExportStatus.complete) {
            if (batch.exactExportErrors?.length) {
                return (
                    <Badge
                        color="yellow"
                        text={getSelectedLanguage().exact.warning}
                    />
                );
            } else {
                return (
                    <Badge
                        color="green"
                        text={getSelectedLanguage().exact.complete}
                    />
                );
            }
        } else if (batch.exactExportStatus === ExactBatchExportStatus.completeWithErrorsOrWarnings) {
            return (
                <Badge
                    color="green"
                    text={getSelectedLanguage().exact.complete}
                />
            );
        } else {
            return (
                <Badge
                    color="orange"
                    text={getSelectedLanguage().exact.uncomplete}
                />
            );
        }
    }

    async function handleDismissExactErrors(runNumber: number | null) {
        if (!runNumber) {
            return;
        }

        await abortableFetch.fetchJson('/api/batches/dismissExactErrors', 'POST', {
            batchType: props.batchType,
            runNumber,
        });

        await loadBatches(state.page);

        setState((prev) => ({ ...prev, modalRunNumber: null }));
    }
}
